/** Freeze the live time windows in a grid's list query string, for a batch over "all
 *  matching" rows.
 *
 * A relative bound (`now-15m`) is resolved by the backend on every request, so a batch
 * dialog's preview and its apply — seconds or minutes apart — would otherwise act on
 * different rows. BatchActionsMenu pins the query once, when an action opens, and both
 * use the pinned copy.
 *
 * A token is pinned to the minute it falls in, written at minute precision: as a lower
 * bound that is `gte` the minute, as an upper bound the whole minute (the end-bound
 * rule) — exactly the set the live query resolves at this moment.
 *
 * What gets pinned:
 * - in the QuerySearch `filters` AST, the bounds of `range` / `gt` / `lt` conditions
 *   (a token is never a legitimate bound of any other kind, and scalar operators are
 *   left alone, so a tag literally named "now" survives);
 * - toolbar time params (`created_after`, `started_to`, `from`, …), never free text.
 */
import { instantToNaiveUtc, parseRelative, relativeMinute } from '@/components/TimeWindowPicker';
import type { WireNode } from '@/components/QuerySearch';

const TIME_PARAM_RE = /(^|_)(after|before|from|to)$/;

function pinToken(value: string, now: Date, sep: ' ' | 'T'): string {
  const rt = parseRelative(value);
  if (!rt) return value;
  const naive = instantToNaiveUtc(relativeMinute(rt, now));
  return sep === ' ' ? naive : `${naive.replace(' ', 'T')}Z`;
}

function pinNode(node: WireNode, now: Date): WireNode {
  if ('conditions' in node)
    return { ...node, conditions: node.conditions.map((n) => pinNode(n, now)) };
  if (node.namespace === '__freetext' || !('operator' in node)) return node;
  const { operator, value } = node;
  if (operator === 'range' && Array.isArray(value)) {
    return { ...node, value: [pinToken(value[0], now, ' '), pinToken(value[1], now, ' ')] };
  }
  if ((operator === 'gt' || operator === 'lt') && typeof value === 'string') {
    return { ...node, value: pinToken(value, now, ' ') };
  }
  return node;
}

export function pinRelativeFilters(filterQuery: string, now: Date = new Date()): string {
  const params = new URLSearchParams(filterQuery);
  let changed = false;
  for (const [key, value] of [...params.entries()]) {
    let next = value;
    if (key === 'filters') {
      try {
        next = JSON.stringify((JSON.parse(value) as WireNode[]).map((n) => pinNode(n, now)));
      } catch {
        // Not ours to judge: an unreadable AST goes out as it came.
      }
    } else if (TIME_PARAM_RE.test(key)) {
      next = pinToken(value, now, 'T');
    }
    if (next !== value) {
      params.set(key, next);
      changed = true;
    }
  }
  return changed ? params.toString() : filterQuery;
}
